import React from 'react';
import { Link } from 'react-router-dom';
import {
    ClockIcon,
    CurrencyDollarIcon,
    Squares2X2Icon,
    UserCircleIcon,
} from '@heroicons/react/24/solid';
import { ClientLogo } from '@/components/ClientLogo';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared';
import { formatCapacityLabel } from '@/features/Collaborators/Allocations/utils/formatCapacityLabel';
import { VacationBadge } from './VacationBadge';
import type { ClientsViewData } from './CollaboratorAllocationsOverviewPage';

interface ClientsViewProps {
    data: ClientsViewData[];
    isLoading: boolean;
}

const formatDateShort = (value: Date | null): string => {
    if (!value || Number.isNaN(value.getTime())) {
        return 'Sem data';
    }
    return value.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatCurrency = (value: number | null | undefined): string => {
    if (value == null || Number.isNaN(value)) {
        return '—';
    }
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const renderSkeletonCard = (_: unknown, index: number) => (
    <article
        key={`client-skeleton-${index}`}
        className="animate-pulse rounded-2xl border border-gray-200 bg-white shadow-sm"
    >
        <div className="border-b border-gray-100 px-6 py-6">
            <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-xl bg-gray-200" />
                <div className="space-y-2">
                    <div className="h-4 w-40 rounded bg-gray-200" />
                    <div className="h-3 w-24 rounded bg-gray-100" />
                </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
                {Array.from({ length: 3 }).map((__, chipIndex) => (
                    <div
                        key={chipIndex}
                        className="h-14 w-28 rounded-xl bg-gray-100"
                    />
                ))}
            </div>
        </div>
        <div className="space-y-4 px-6 py-6">
            {Array.from({ length: 2 }).map((__, initiativeIndex) => (
                <div key={initiativeIndex} className="space-y-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-4">
                    <div className="h-4 w-48 rounded bg-gray-200" />
                    {Array.from({ length: 2 }).map((___, memberIndex) => (
                        <div key={memberIndex} className="h-16 rounded-lg bg-white" />
                    ))}
                </div>
            ))}
        </div>
    </article>
);

const ClientsView: React.FC<ClientsViewProps> = ({ data, isLoading }) => {
    if (isLoading) {
        return (
            <section className="space-y-6">
                {Array.from({ length: 2 }).map(renderSkeletonCard)}
            </section>
        );
    }

    if (data.length === 0) {
        return (
            <section className="rounded-lg border border-dashed border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-500">
                Nenhum cliente com alocações ativas no momento.
            </section>
        );
    }

    return (
        <section className="space-y-8">
            {data.map((client, clientIndex) => (
                <article
                    key={`${client.clientId ?? client.clientName}-${clientIndex}`}
                    className="rounded-2xl border border-gray-200 bg-white shadow-sm"
                >
                    <header className="flex flex-col gap-5 border-b border-gray-100 px-6 py-6 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-center gap-4">
                            <ClientLogo
                                client={{ id: client.clientId, name: client.clientName, logoUrl: client.clientLogo ?? null }}
                                clientId={client.clientId ?? undefined}
                                variant={64}
                                className="h-14 w-14 rounded-xl ring-0 md:h-16 md:w-16"
                            />
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">{client.clientName}</h3>
                                <p className="text-sm text-gray-500">
                                    {client.totalPeople} colaborador{client.totalPeople === 1 ? '' : 'es'} alocado{client.totalPeople === 1 ? '' : 's'}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <div className="min-w-[150px] rounded-xl border border-indigo-200 bg-indigo-100 px-5 py-4 text-indigo-900 shadow-sm">
                                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Total</p>
                                <p className="text-xl font-semibold">{client.totalPeople}</p>
                                <p className="text-[11px] text-indigo-500">Colaboradores</p>
                            </div>
                            {client.allocationsByType.map((typeStat) => {
                                const TypeIcon = typeStat.icon;
                                return (
                                    <div
                                        key={typeStat.type}
                                        className="flex min-w-[170px] items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm"
                                    >
                                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                                            <TypeIcon className="h-4 w-4" />
                                        </span>
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{typeStat.label}</p>
                                            <p className="text-lg font-semibold text-slate-900">{typeStat.count}</p>
                                            <p className="text-[11px] text-slate-500">Alocaç{typeStat.count === 1 ? 'ão' : 'ões'}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </header>

                    <div className="space-y-8 px-6 py-6">
                        {client.initiatives.map((initiative, initiativeIndex) => (
                            <section
                                key={`${initiative.name}-${initiativeIndex}`}
                                className="rounded-xl border border-gray-100 bg-gray-50 p-4 sm:p-5"
                            >
                                <header className="flex flex-col gap-2 xl:flex-row xl:items-start xl:justify-between">
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-800">{initiative.name}</h4>
                                        <p className="text-xs text-gray-500">
                                            {initiative.totalEntries} colaborador{initiative.totalEntries === 1 ? '' : 'es'}
                                        </p>
                                    </div>
                                </header>

                                <ul className="mt-4 space-y-4">
                                    {initiative.members.map((member) => {
                                        const MemberTypeIcon = member.allocationTypeIcon ?? Squares2X2Icon;
                                        const capacityChipLabel = formatCapacityLabel(member);
                                        return (
                                            <li
                                                key={member.entryId}
                                                className="relative rounded-lg border border-gray-100 bg-white px-4 py-4 shadow-sm transition hover:border-indigo-200 hover:shadow-xl cursor-pointer"
                                                >
                                                    {member.collaboratorId && (
                                                        <Link
                                                            to={`/collaborators/${member.collaboratorId}/allocations`}
                                                            className="absolute inset-0 z-10 rounded-lg"
                                                            aria-label={`Abrir alocações de ${member.collaboratorName}`}
                                                        />
                                                    )}
                                                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                                                        <div className="flex items-start gap-3">
                                                            <CollaboratorAvatar
                                                                photoUrl={member.collaboratorAvatar ?? undefined}
                                                                name={member.collaboratorName}
                                                                size="small"
                                                            />
                                                            <div className="space-y-1">
                                                                <p className="text-sm font-semibold text-gray-900">{member.collaboratorName}</p>
                                                                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                                                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-600">
                                                                        <MemberTypeIcon className="h-3.5 w-3.5" />
                                                                        {member.allocationTypeLabel}
                                                                    </span>
                                                                    {capacityChipLabel && (
                                                                        <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-1 text-[11px] font-semibold text-primary-600">
                                                                            <Squares2X2Icon className="h-3.5 w-3.5" />
                                                                            Papel: {capacityChipLabel}
                                                                        </span>
                                                                    )}
                                                                    {member.dedication && (
                                                                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                                                                            <ClockIcon className="h-3 w-3" />
                                                                            {member.dedication}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                    <div className="grid grid-cols-1 gap-4 text-xs text-gray-600 md:grid-cols-2 md:text-right">
                                                        <div className="flex flex-col gap-1 md:items-start">
                                                            <span className="font-semibold uppercase tracking-wide text-gray-500">Início</span>
                                                            <span className="text-sm font-medium text-gray-800">{formatDateShort(member.startDate)}</span>
                                                        </div>
                                                        <div className="flex flex-col gap-1 md:items-end">
                                                            <span className="font-semibold uppercase tracking-wide text-gray-500">Fim</span>
                                                            <span className="text-sm font-medium text-gray-800">{formatDateShort(member.endDate)}</span>
                                                        </div>
                                                        <div className="flex flex-col gap-1 md:items-start">
                                                            <span className="font-semibold uppercase tracking-wide text-gray-500">Taxa</span>
                                                            <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-800">
                                                                <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                                                                {formatCurrency(member.rate)}
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-col gap-1 min-w-[160px] md:items-end">
                                                            <span className="font-semibold uppercase tracking-wide text-gray-500">Responsável</span>
                                                            <span className="flex items-center gap-1 text-sm font-medium text-gray-800 md:justify-end">
                                                                <UserCircleIcon className="h-4 w-4 text-gray-400" />
                                                                {member.clientResponsible ?? 'Não informado'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="mt-4">
                                                        <VacationBadge
                                                            summary={member.vacationSummary}
                                                            allocationId={member.entryId}
                                                            className="text-sm p-2"
                                                        />
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </section>
                        ))}
                    </div>
                </article>
            ))}
        </section>
    );
};

export default ClientsView;
